import { Fragment, useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { customerSupportAdminService } from '../../services'
import ShopAutocomplete from './ShopAutocomplete'
import {
  REPORT_DATE_PRESETS,
  resolveReportDatePreset,
} from './reportDatePresets'

const money = (v) =>
  `৳${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const signedMoney = (v) => {
  const n = Number(v) || 0
  if (Math.abs(n) < 0.005) return money(0)
  return n > 0 ? `+${money(n)}` : `-${money(Math.abs(n))}`
}

function lineHasVendorFunding(line) {
  return Math.max(0, Number(line?.vendorFundedAmount) || 0) > 0
}

function resolveSimpleCostToGrocer(line) {
  const lot = Number(line?.cost)
  if (Number.isFinite(lot)) return lot
  const after = Number(line?.costAfterReimbursement)
  if (Number.isFinite(after)) return after
  const before = Number(line?.costBeforeReimbursement)
  if (Number.isFinite(before)) return before
  return 0
}

function ProfitLineEconomics({ line }) {
  const showReimbursementFields = lineHasVendorFunding(line)
  const finalProfitLabel = line.isNegative
    ? 'Total Profit'
    : showReimbursementFields
      ? 'Net Profit'
      : 'Total Profit'
  const fields = [
    ['Sale Qty', String(Number(line.saleQuantity) || 0)],
    ['Return Qty', String(Number(line.returnQuantity) || 0)],
    ['Unit Cost', money(line.unitCost ?? line.unit_cost)],
    ['Unit Price', money(line.unitMrp ?? line.unit_mrp)],
    ['Promotion Amount', money(line.promotionalValue)],
    ['Grocer Discount', money(line.discount)],
    ['Vendor Funded Amount', money(line.vendorFundedAmount)],
    ...(showReimbursementFields
      ? [
          ['Shwapno Reimbursement', money(line.shwapnoReimbursement)],
          [
            'Cost to Grocer (Before Reimbursement)',
            money(line.costBeforeReimbursement),
          ],
          [
            'Cost to Grocer (After Reimbursement)',
            money(line.costAfterReimbursement),
          ],
        ]
      : [['Cost to Grocer', money(resolveSimpleCostToGrocer(line))]]),
    ['Total Selling', money(line.selling)],
    ...(showReimbursementFields && !line.isNegative
      ? [
          [
            'Profit Before Reimbursement',
            signedMoney(line.profitBeforeReimbursement),
          ],
        ]
      : []),
    [finalProfitLabel, signedMoney(line.profit)],
  ]
  return (
    <Box sx={{ pl: 6, pr: 2, py: 1.5, bgcolor: '#FAFBFC' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {line.productName}
        {line.sku ? (
          <Typography component="span" variant="caption" color="text.secondary">
            {` · ${line.sku}`}
          </Typography>
        ) : null}
        {line.isNegative ? (
          <Chip size="small" label="Non-inventory" sx={{ ml: 1 }} />
        ) : null}
      </Typography>
      <Grid container spacing={1}>
        {fields.map(([label, value]) => (
          <Grid item xs={12} sm={6} md={4} key={label}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

function ProfitInvoiceRow({ invoice }) {
  const [open, setOpen] = useState(false)
  return (
    <Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell width={48}>
          <IconButton
            size="small"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse invoice' : 'Expand invoice'}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{invoice.orderId}</TableCell>
        <TableCell>{invoice.invoiceNumber || '—'}</TableCell>
        <TableCell>
          {invoice.orderDate
            ? new Date(invoice.orderDate).toLocaleString()
            : '—'}
        </TableCell>
        <TableCell>{invoice.orderStatus || '—'}</TableCell>
        <TableCell align="right">{signedMoney(invoice.invoiceProfit)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {(invoice.lines || []).map((line) => (
              <ProfitLineEconomics
                key={line.lineId || `${invoice.orderId}-${line.sku}`}
                line={line}
              />
            ))}
            {!invoice.lines?.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No line items
              </Typography>
            ) : null}
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

const todayPreset = resolveReportDatePreset('today') || {
  startDate: '',
  endDate: '',
}

export default function ReportsVerificationPanel({
  onError,
  onInfo,
  onExportRequest,
}) {
  const [shop, setShop] = useState(null)
  const [preset, setPreset] = useState('today')
  const [startDate, setStartDate] = useState(todayPreset.startDate)
  const [endDate, setEndDate] = useState(todayPreset.endDate)

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const [expanded, setExpanded] = useState(false)
  const [sectionLoading, setSectionLoading] = useState('')
  const [invoices, setInvoices] = useState(null)
  const [profitDetail, setProfitDetail] = useState(null)
  const [products, setProducts] = useState(null)
  const [promotions, setPromotions] = useState(null)
  const [returnsData, setReturnsData] = useState(null)
  const [invPage, setInvPage] = useState(0)
  const [profitPage, setProfitPage] = useState(0)
  const [prodPage, setProdPage] = useState(0)
  const [promoPage, setPromoPage] = useState(0)
  const [retPage, setRetPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [sectionSearch, setSectionSearch] = useState('')

  useEffect(() => {
    if (preset === 'custom') return
    const range = resolveReportDatePreset(preset)
    if (range) {
      setStartDate(range.startDate)
      setEndDate(range.endDate)
    }
  }, [preset])

  const baseParams = () => ({
    orgId: Number(shop.id),
    startDate,
    endDate,
  })

  const loadSummary = async () => {
    if (!shop?.id || !startDate || !endDate) {
      onError?.('Select a shop and date range')
      return
    }
    setSummaryLoading(true)
    onError?.('')
    try {
      const data = await customerSupportAdminService.reportsMirror(baseParams())
      setSummary(data)
      setInvoices(null)
      setProfitDetail(null)
      setProducts(null)
      setPromotions(null)
      setReturnsData(null)
      setExpanded(false)
      onInfo?.(
        `Summary loaded for ${shop.label} (${startDate} → ${endDate})`,
      )
    } catch (err) {
      onError?.(
        err?.response?.data?.message || err.message || 'Failed to load summary',
      )
    } finally {
      setSummaryLoading(false)
    }
  }

  const loadSection = async (section, page = 0) => {
    if (!shop?.id || !startDate || !endDate) return
    setSectionLoading(section)
    onError?.('')
    try {
      const params = {
        ...baseParams(),
        page: page + 1,
        limit: rowsPerPage,
        search: sectionSearch.trim() || undefined,
        sortOrder: 'DESC',
      }
      if (section === 'invoices') {
        setInvoices(await customerSupportAdminService.reportsInvoices(params))
        setInvPage(page)
      } else if (section === 'profit-detail') {
        setProfitDetail(
          await customerSupportAdminService.reportsProfitDetail(params),
        )
        setProfitPage(page)
      } else if (section === 'products') {
        setProducts(await customerSupportAdminService.reportsProducts(params))
        setProdPage(page)
      } else if (section === 'promotions') {
        setPromotions(
          await customerSupportAdminService.reportsPromotions(params),
        )
        setPromoPage(page)
      } else if (section === 'returns') {
        setReturnsData(await customerSupportAdminService.reportsReturns(params))
        setRetPage(page)
      }
    } catch (err) {
      onError?.(
        err?.response?.data?.message ||
          err.message ||
          `Failed to load ${section}`,
      )
    } finally {
      setSectionLoading('')
    }
  }

  const onExpand = (panel) => async (_e, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
    if (!isExpanded || !summary) return
    if (panel === 'invoices' && !invoices) await loadSection('invoices', 0)
    if (panel === 'profit-detail' && !profitDetail) {
      await loadSection('profit-detail', 0)
    }
    if (panel === 'products' && !products) await loadSection('products', 0)
    if (panel === 'promotions' && !promotions) await loadSection('promotions', 0)
    if (panel === 'returns' && !returnsData) await loadSection('returns', 0)
  }

  const s = summary?.summary

  return (
    <Stack spacing={2}>
      <ShopAutocomplete value={shop} onChange={setShop} required />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Quick date range
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={preset}
          onChange={(_, v) => v && setPreset(v)}
          sx={{ flexWrap: 'wrap', gap: 0.5 }}
        >
          {REPORT_DATE_PRESETS.map((p) => (
            <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none', px: 1.5 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'flex-end' }}
      >
        <TextField
          size="small"
          type="date"
          label="Start"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => {
            setPreset('custom')
            setStartDate(e.target.value)
          }}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <TextField
          size="small"
          type="date"
          label="End"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => {
            setPreset('custom')
            setEndDate(e.target.value)
          }}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Button
          variant="contained"
          size="large"
          onClick={() => void loadSummary()}
          disabled={!shop || !startDate || !endDate || summaryLoading}
          sx={{ minWidth: 200, height: 40, px: 3, flexShrink: 0 }}
        >
          {summaryLoading ? 'Loading…' : 'Load grocer view'}
        </Button>
        <Button
          variant="outlined"
          size="large"
          disabled={!shop || !startDate || !endDate}
          onClick={() =>
            onExportRequest?.({
              orgId: Number(shop.id),
              startDate,
              endDate,
              search: sectionSearch.trim() || undefined,
            })
          }
          sx={{ minWidth: 140, height: 40, flexShrink: 0 }}
        >
          Export CSV
        </Button>
      </Stack>

      {s ? (
        <>
          <Grid container spacing={2}>
            {[
              ['Total Sales', money(s.totalSales)],
              ['Inventory Sales', money(s.inventorySales)],
              ['Non Inventory Sales', money(s.negativeSales)],
              [
                'Shwapno Reimbursement (Return amount from Promotions)',
                money(s.totalReimbursementAmount),
              ],
              ['Total Profit', money(s.profit)],
              [
                'Total Inventory Profit (after Shwapno Reimbursement)',
                money(s.inventoryProfit),
              ],
              ['Total Non-Inventory Profit', money(s.nonInventoryProfit)],
              ['Invoices', String(s.orders ?? 0)],
              ['Average Order', money(s.avgOrder)],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h6">{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Search within subsections"
              value={sectionSearch}
              onChange={(e) => setSectionSearch(e.target.value)}
              fullWidth
              helperText="Applied when you open or refresh a subsection"
            />
            <TextField
              size="small"
              type="number"
              label="Rows / page"
              value={rowsPerPage}
              onChange={(e) =>
                setRowsPerPage(Math.max(5, Math.min(100, Number(e.target.value) || 20)))
              }
              sx={{ width: 120 }}
            />
          </Stack>

          <Accordion
            expanded={expanded === 'invoices'}
            onChange={onExpand('invoices')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Invoice-wise</Typography>
              {sectionLoading === 'invoices' ? (
                <CircularProgress size={16} sx={{ ml: 2 }} />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              <Button
                size="small"
                sx={{ mb: 1 }}
                onClick={() => void loadSection('invoices', invPage)}
              >
                Refresh
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoices?.items || []).map((row) => (
                    <TableRow key={row.orderId}>
                      <TableCell>{row.orderId}</TableCell>
                      <TableCell>
                        {row.saleAt || row.orderDate
                          ? new Date(row.saleAt || row.orderDate).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell>{row.orderStatus}</TableCell>
                      <TableCell>{row.paymentStatus}</TableCell>
                      <TableCell align="right">{money(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {!invoices?.items?.length ? (
                    <TableRow>
                      <TableCell colSpan={5}>No invoices</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={invoices?.pagination?.total || 0}
                page={invPage}
                onPageChange={(_, p) => void loadSection('invoices', p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'profit-detail'}
            onChange={onExpand('profit-detail')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>
                Profit Breakdown (per invoice)
              </Typography>
              {sectionLoading === 'profit-detail' ? (
                <CircularProgress size={16} sx={{ ml: 2 }} />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              {profitDetail?.summary ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 1, flexWrap: 'wrap' }}
                >
                  <Chip
                    label={`Total Profit ${money(
                      profitDetail.summary.totalProfit,
                    )}`}
                  />
                  <Chip
                    label={`Inventory Profit ${money(
                      profitDetail.summary.inventoryProfit,
                    )}`}
                  />
                  <Chip
                    label={`Non-Inventory Profit ${money(
                      profitDetail.summary.nonInventoryProfit,
                    )}`}
                  />
                </Stack>
              ) : null}
              <Alert severity="info" sx={{ mb: 1 }}>
                Expand an invoice to see Unit Cost, Unit Price, Promotion,
                Grocer Discount, Vendor Funded Amount, Shwapno Reimbursement,
                costs before/after reimbursement when vendor funded, and Net /
                Total Profit.
                Search matches invoice #, invoice number, product name, or SKU.
              </Alert>
              <Button
                size="small"
                sx={{ mb: 1 }}
                onClick={() => void loadSection('profit-detail', profitPage)}
              >
                Refresh
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Order</TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Invoice Profit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(profitDetail?.items || []).map((row) => (
                    <ProfitInvoiceRow key={row.orderId} invoice={row} />
                  ))}
                  {!profitDetail?.items?.length ? (
                    <TableRow>
                      <TableCell colSpan={6}>No profit rows</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={profitDetail?.pagination?.total || 0}
                page={profitPage}
                onPageChange={(_, p) => void loadSection('profit-detail', p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'products'}
            onChange={onExpand('products')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Product-wise</Typography>
              {sectionLoading === 'products' ? (
                <CircularProgress size={16} sx={{ ml: 2 }} />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              {products?.summary ? (
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Total ${money(products.summary.totalSales)}`} />
                  <Chip
                    label={`Reimbursement ${money(
                      products.summary.totalReimbursementAmount,
                    )}`}
                  />
                </Stack>
              ) : null}
              <Button
                size="small"
                sx={{ mb: 1 }}
                onClick={() => void loadSection('products', prodPage)}
              >
                Refresh
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Sales</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(products?.items || []).map((row) => (
                    <TableRow key={`${row.productId || row.sku}-${row.productName}`}>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.sku || row.articleCode || '—'}</TableCell>
                      <TableCell align="right">
                        {row.totalQuantitySold ?? row.unitsSold ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        {money(row.totalSalesAmount ?? row.netSales)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!products?.items?.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>No products</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={products?.pagination?.total || 0}
                page={prodPage}
                onPageChange={(_, p) => void loadSection('products', p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'promotions'}
            onChange={onExpand('promotions')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Promotions / reimbursement</Typography>
              {sectionLoading === 'promotions' ? (
                <CircularProgress size={16} sx={{ ml: 2 }} />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              {promotions?.summary ? (
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Reimbursement ${money(
                      promotions.summary.totalReimbursementAmount,
                    )}`}
                  />
                  <Chip
                    label={`Net promo ${money(promotions.summary.netPromoValue)}`}
                  />
                  <Chip
                    label={`Units ${promotions.summary.totalGrossPromoUnitsSold ?? 0}`}
                  />
                </Stack>
              ) : null}
              <Button
                size="small"
                sx={{ mb: 1 }}
                onClick={() => void loadSection('promotions', promoPage)}
              >
                Refresh
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Sold</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Net promo</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(promotions?.items || []).map((row, idx) => (
                    <TableRow key={`${row.sku}-${idx}`}>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.sku}</TableCell>
                      <TableCell align="right">{row.unitsSold}</TableCell>
                      <TableCell align="right">{row.unitsReturned}</TableCell>
                      <TableCell align="right">
                        {money(row.netPromoValue)}
                      </TableCell>
                      <TableCell>{row.reimbursementReason || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!promotions?.items?.length ? (
                    <TableRow>
                      <TableCell colSpan={6}>No promo lines</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={promotions?.pagination?.total || 0}
                page={promoPage}
                onPageChange={(_, p) => void loadSection('promotions', p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'returns'}
            onChange={onExpand('returns')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Returns</Typography>
              {sectionLoading === 'returns' ? (
                <CircularProgress size={16} sx={{ ml: 2 }} />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              {returnsData?.overview ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Returns overview loaded for this range. Order-wise and
                  product-wise lists below.
                </Alert>
              ) : null}
              <Button
                size="small"
                sx={{ mb: 1 }}
                onClick={() => void loadSection('returns', retPage)}
              >
                Refresh
              </Button>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                Order-wise returns
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Return order</TableCell>
                    <TableCell>Parent sale</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(returnsData?.orderWise?.items || []).map((row) => (
                    <TableRow key={row.orderId}>
                      <TableCell>{row.orderId}</TableCell>
                      <TableCell>{row.parentOrderId || '—'}</TableCell>
                      <TableCell>
                        {row.saleAt || row.orderDate
                          ? new Date(row.saleAt || row.orderDate).toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell align="right">{money(row.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {!returnsData?.orderWise?.items?.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>No returns</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={returnsData?.orderWise?.pagination?.total || 0}
                page={retPage}
                onPageChange={(_, p) => void loadSection('returns', p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <Alert severity="info">
          Choose a shop and date range, then load the grocer view. Invoice,
          profit breakdown, product, promotions, and returns subsections load
          when you open them.
        </Alert>
      )}
    </Stack>
  )
}
